<template>
  <div
    :class="[
      $style.base,
      {
        [$style.isSmall]: small,
        [$style.addPaddingRight]: !!localValue || forceCloseButton
      }
    ]"
  >
    <div :class="$style.icon">
      <BaseSpinner v-if="debounce && isLoading" />
      <BaseIcon
        v-else
        name="search"
        :size="small ? 's' : 'm'"
        v-test="'base-search-icon'"
      />
    </div>
    <div
      ref="input"
      :class="$style.input"
    >
      <BaseInput
        v-model="localValue"
        :placeholder="placeholder || `${$t('global.actions.search')}…`"
        type="search"
        :focus="focus"
        :required="required"
        :size="small ? 's' : 'm'"
        :error="error"
        @focus="onSearchFocus"
        @blur="onSearchBlur"
        v-test="'baseSearch'"
      />
      <div
        v-show="localValue || forceCloseButton"
        :class="$style.close"
        @click="removeQuery"
        v-test="'search-close-button'"
      >
        <BaseIcon
          name="close"
          :size="small ? 's' : 'm'"
        />
      </div>
    </div>
    <div
      ref="history"
      :class="$style.history"
    >
      <SearchHistory
        v-if="historyKey"
        :show="!localValue && showCustomerSearchHistory"
        :historyKey="historyKey"
        :searchHistory="searchHistory"
        @select="(item) => selectHistoryItem(item)"
        @delete="(item) => deleteHistoryItem(item)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { useStorage } from '@vueuse/core';
import { defineComponent, ref } from 'vue';
import { hideTooltip, showTooltip } from '@/helpers/ui';
import SearchHistory from './SearchHistory.vue';

export default defineComponent({
  name: 'BaseSearch',
  components: {
    SearchHistory,
  },
  inheritAttrs: false,
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
    },
    focus: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    debounce: {
      type: [Boolean, Number],
      default: false,
    },
    error: String,
    required: {
      type: Boolean,
      default: false,
    },
    historyKey: {
      type: String,
    },
    forceCloseButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'focus', 'blur', 'clear'],
  setup(props) {
    const searchHistory: Ref<string[]> = props.historyKey
      ? useStorage(props.historyKey, [])
      : ref([]);
    const maxLength = 10;

    const addItem = (item: string) => {
      // only save actual strings
      if (!item || !props.historyKey) {
        return;
      }

      // Remove item from history
      searchHistory.value = searchHistory.value.filter(
        (element) => element !== item,
      );

      // Add item to start of history
      if (
        searchHistory.value.length &&
        item.startsWith(searchHistory.value[0])
      ) {
        searchHistory.value[0] = item;
      } else {
        searchHistory.value.unshift(item);
      }

      // Limit size of history
      if (searchHistory.value.length > maxLength) {
        searchHistory.value = searchHistory.value.splice(0, maxLength);
      }
    };

    const deleteItem = (item: string) => {
      if (!item || !props.historyKey) {
        return;
      }
      // Remove item from history
      searchHistory.value = searchHistory.value.filter(
        (element) => element !== item,
      );
    };

    return {
      addItem,
      deleteItem,
      searchHistory,
    };
  },
  data() {
    return {
      isTyping: null,
      tooltipTimeout: null,
      showCustomerSearchHistory: false,
      isLoading: false,
    };
  },
  computed: {
    localValue: {
      get() {
        return this.modelValue;
      },
      set(value: string) {
        if (this.isTyping) {
          clearTimeout(this.isTyping);
          this.isLoading = false;
        }

        if (this.tooltipTimeout) {
          clearTimeout(this.tooltipTimeout);
          hideTooltip();
        }

        const emitValue = () => {
          if (this.debounce && value?.length === 1) {
            this.tooltipTimeout = setTimeout(() => {
              showTooltip(
                this.$t('search.one_character_tooltip'),
                this.$refs.input,
              );
            }, 800);

            return;
          }

          if (value && this.historyKey) {
            this.addItem(value);
          }
          this.$emit('update:modelValue', value);
        };

        if (this.debounce && value) {
          this.isLoading = true;
          this.isTyping = setTimeout(
            () => {
              emitValue();
              this.isLoading = false;
            },
            typeof this.debounce === 'number' ? this.debounce : 700,
          );
        } else {
          emitValue();
        }
      },
    },
  },
  methods: {
    removeQuery() {
      this.showCustomerSearchHistory = false;
      this.localValue = '';
      this.$emit('clear');
    },
    onSearchFocus() {
      this.showCustomerSearchHistory = true;
      this.$emit('focus');
    },
    onSearchBlur() {
      if (this.showCustomerSearchHistory) {
        setTimeout(() => {
          this.showCustomerSearchHistory = false;
        }, 200);
      }

      if (this.tooltipTimeout) {
        clearTimeout(this.tooltipTimeout);
        hideTooltip();
      }
    },
    selectHistoryItem(value: string) {
      if (value && this.historyKey) {
        this.addItem(value);
      }
      this.$emit('update:modelValue', value);
      this.showCustomerSearchHistory = false;
    },
    deleteHistoryItem(value: string) {
      setTimeout(() => {
        this.showCustomerSearchHistory = true;
      }, 250);
      if (value && this.historyKey) {
        this.deleteItem(value);
      }
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  position: relative;
  min-width: 140px;
  max-width: 100%;
}

.input {
  position: relative;

  input {
    padding-left: 36px !important;

    .base.addPaddingRight & {
      padding-right: 28px !important;
    }
  }
}

.icon,
.close {
  position: absolute;
  top: 0;
  width: 20px;
  z-index: 1;
  display: flex;
  align-items: center;

  .base.isSmall & {
    height: $input-height-small;
  }

  .base:not(.isSmall) & {
    height: $input-height;
  }
}

.icon {
  left: 12px;
  pointer-events: none;
}

.close {
  padding: $spacing * 0.5 $spacing * 1.75 $spacing * 0.5 0;
  cursor: pointer;
  right: 0;
}
</style>
